import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Body1Strong,
  Card,
} from '@fluentui/react-components';

import { MouseEventHandler, ReactNode } from 'react';

import TableItem from './TableItem';
import type { EFolderType, TOnMove } from '@modules/items/types/folders.types';

import type {
  TDisableColumn,
  TColumnLabels,
  TItemsTableRow,
  TResolvedItemNames,
  TShowColumn,
} from '../../types/items.types';
import useItemTableStyles from '../../hooks/useItemTableStyles.hook';
import { ITableColumnSizingState, TGetTableHeaderCellProps } from '@shared/types';
import type { SmartTableCellProps } from '@shared/components/tables/SmartTableCell';

type TProps = {
  rows: TItemsTableRow[];
  resolvedNames?: TResolvedItemNames;
  columnSizing?: ITableColumnSizingState;
  dateType?: 'modified' | 'viewed' | 'archived';
  // Shows the modified date as "2 days ago" instead of a calendar date.
  relativeDate?: boolean;
  show?: TShowColumn;
  disable?: TDisableColumn; // columns that should be disabled
  labels?: TColumnLabels;
  selectionHeaderCell?: ReactNode;
  openMode?: SmartTableCellProps['openMode']; // default: folders open in place, other items in a new tab
  onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
  onMove?: TOnMove;
  folderType?: EFolderType;
  ariaLabel?: string;
  appearance?: 'card' | 'plain'; // tables in dialogs use 'plain'
};

const getTableHeaderCellDefaultProps: TGetTableHeaderCellProps = (columnId) => {
  switch (columnId) {
    case 'name':
      return { style: { width: '250px' } };
    case 'authors':
      return { style: { width: '90px' } };
    default:
      return { style: { width: '100px' } };
  }
};

const DEFAULT_SHOW = {
  products: false, // "country registrations"
  authors: true,
  status: true,
  date: true, // modified/viewed
  location: false, // unique for "search" table
  archivedBy: false, // unique for "archive" table
  version: false,
  approvedVersion: true,
  approvalOpenSince: false,
  reviewDue: false,
  reviewer: false,
  submissionDue: false,
  openApprovals: false,
};

const DEFAULT_DISABLE = {
  name: false,
  date: true,
  approvedVersion: true,
  version: true,
  // unique for table type
  products: false, // "country registrations"
  preview: false, // "blocks"
  location: false, // "search"
  archivedBy: false, // "archive"
};
const DEFAULT_LABELS = {
  name: 'Name',
  version: 'Latest version',
};

const ItemsTable = ({
  rows,
  resolvedNames,
  columnSizing,
  dateType = 'modified',
  relativeDate = false,
  show = {},
  disable = {},
  labels = {},
  selectionHeaderCell = null,
  openMode,
  onLinkClick = (e) => undefined,
  onMove,
  folderType,
  ariaLabel,
  appearance = 'card',
}: TProps) => {
  // Merge default values with provided props
  show = { ...DEFAULT_SHOW, ...show };
  disable = { ...DEFAULT_DISABLE, ...disable };
  labels = { ...DEFAULT_LABELS, ...labels };

  if (show.location && !resolvedNames) {
    console.error('Resolved names are required when showing the location column.');
    // showToast('error', 'Something went wrong. The location column cannot be displayed.');
  }

  const getTableHeaderCellProps = columnSizing?.getTableHeaderCellProps || getTableHeaderCellDefaultProps;

  const dateTypeLabel = dateType === 'modified' ? 'Modified' : dateType === 'viewed' ? 'Opened' : 'Archived';

  const classes = useItemTableStyles();
  const isCard = appearance === 'card';
  const Wrapper = isCard ? Card : 'div';
  return (
    <Wrapper className={isCard ? classes.card : undefined}>
      <Table aria-label={ariaLabel} className={isCard ? classes.table : undefined} {...columnSizing?.getTableProps()}>
        <TableHeader>
          <TableRow>
            {selectionHeaderCell}
            <TableHeaderCell {...getTableHeaderCellProps('name')}>
              <Body1Strong>{labels.name}</Body1Strong>
            </TableHeaderCell>

            {show.approvalOpenSince && (
              <TableHeaderCell {...getTableHeaderCellProps('approvalOpenSince')}>
                <Body1Strong>Approval open since</Body1Strong>
              </TableHeaderCell>
            )}

            {show.reviewDue && (
              <TableHeaderCell {...getTableHeaderCellProps('reviewDue')}>
                <Body1Strong>{labels.reviewDue ?? 'Due'}</Body1Strong>
              </TableHeaderCell>
            )}

            {show.submissionDue && (
              <TableHeaderCell {...getTableHeaderCellProps('submissionDue')}>
                <Body1Strong>Submission due</Body1Strong>
              </TableHeaderCell>
            )}

            {show.openApprovals && (
              <TableHeaderCell {...getTableHeaderCellProps('openApprovals')}>
                <Body1Strong>Open approvals</Body1Strong>
              </TableHeaderCell>
            )}

            {show.products && (
              <TableHeaderCell {...getTableHeaderCellProps('products')}>
                <Body1Strong>Products</Body1Strong>
              </TableHeaderCell>
            )}

            {show.location && resolvedNames && (
              <TableHeaderCell {...getTableHeaderCellProps('location')}>
                <Body1Strong>Location</Body1Strong>
              </TableHeaderCell>
            )}

            {show.preview && (
              <TableHeaderCell {...getTableHeaderCellProps('preview')}>
                <Body1Strong>Preview</Body1Strong>
              </TableHeaderCell>
            )}

            {show.date && (
              <TableHeaderCell {...getTableHeaderCellProps(dateType)}>
                <Body1Strong>{labels.date ?? dateTypeLabel}</Body1Strong>
              </TableHeaderCell>
            )}

            {show.status && (
              <TableHeaderCell {...getTableHeaderCellProps('status')}>
                <Body1Strong>Status</Body1Strong>
              </TableHeaderCell>
            )}

            {show.version && (
              <TableHeaderCell {...getTableHeaderCellProps('latestVersionId')}>
                <Body1Strong>{labels.version}</Body1Strong>
              </TableHeaderCell>
            )}

            {show.approvedVersion && (
              <TableHeaderCell {...getTableHeaderCellProps('approvedVersionId')}>
                <Body1Strong>Approved version</Body1Strong>
              </TableHeaderCell>
            )}

            {show.authors && (
              <TableHeaderCell {...getTableHeaderCellProps('authors')}>
                <Body1Strong>Authors</Body1Strong>
              </TableHeaderCell>
            )}

            {show.reviewer && (
              <TableHeaderCell {...getTableHeaderCellProps('reviewer')}>
                <Body1Strong>Reviewer</Body1Strong>
              </TableHeaderCell>
            )}

            {show.archivedBy && (
              <TableHeaderCell {...getTableHeaderCellProps('archivedBy')}>
                <Body1Strong>Archived by</Body1Strong>
              </TableHeaderCell>
            )}
          </TableRow>
        </TableHeader>

        <TableBody className={isCard ? classes.tableBody : undefined}>
          {rows.map((row) => (
            <TableItem
              resolvedNames={resolvedNames}
              dateType={dateType}
              relativeDate={relativeDate}
              show={show}
              disable={disable}
              openMode={openMode}
              onLinkClick={onLinkClick}
              onMove={onMove}
              columnSizing={columnSizing}
              folderType={folderType}
              {...row}
              key={row.key}
            />
          ))}
        </TableBody>
      </Table>
    </Wrapper>
  );
};

export default ItemsTable;
